// Video Generation Modal - генерация видео (Seedance, WAN, Hailuo)

import React, { useState, useEffect } from 'react';
import { scenesApi, uploadApi, getFullUrlWithTimestamp } from '../../services/api';
import type { VideoSettings } from '../../types';

interface VideoModalProps {
  isOpen: boolean;
  projectId: string;
  sceneNumber: number;
  firstFrameUrl?: string;
  scenePrompt?: string;
  onClose: () => void;
  onTaskCreated: (taskId: string) => void;
}

const VideoModal: React.FC<VideoModalProps> = ({
  isOpen,
  projectId,
  sceneNumber,
  firstFrameUrl,
  scenePrompt,
  onClose,
  onTaskCreated,
}) => {
  const [model, setModel] = useState<VideoSettings['model']>('seedance-t2v');
  const [prompt, setPrompt] = useState(scenePrompt || '');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [clipType, setClipType] = useState('none');
  const [cameraControl, setCameraControl] = useState('none');

  // Seedance settings
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [resolution, setResolution] = useState('1080p');
  const [duration, setDuration] = useState(5);
  const [cameraFixed, setCameraFixed] = useState(false);
  const [seed, setSeed] = useState(-1);

  // WAN settings
  const [negativePrompt, setNegativePrompt] = useState('');
  const [promptExpansion, setPromptExpansion] = useState(true);

  // Hailuo settings
  const [promptOptimizer, setPromptOptimizer] = useState(true);

  const [generating, setGenerating] = useState(false);

  // Update prompt when modal opens or scene changes
  useEffect(() => {
    if (isOpen) {
      if (scenePrompt) {
        setPrompt(scenePrompt);
      }
      // Сбросить состояние generating при открытии модала
      setGenerating(false);
    }
  }, [scenePrompt, isOpen]);

  if (!isOpen) return null;

  const isI2V = model.includes('i2v');
  const hasFirstFrame = !!firstFrameUrl;
  const isWAN = model.startsWith('wan');
  const isHailuo = model.startsWith('hailuo');

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      alert('Please enter a video prompt');
      return;
    }

    if (isI2V && !imageFile && !hasFirstFrame) {
      alert('Please upload an image or generate a first frame');
      return;
    }

    setGenerating(true);

    try {
      let imageUrl = firstFrameUrl;
      let audioUrl;

      // Upload image if provided
      if (imageFile && !hasFirstFrame) {
        const uploadResult = await uploadApi.uploadTempFile(imageFile);
        if (uploadResult.success) {
          imageUrl = uploadResult.url;
        }
      }

      // Upload audio if provided (for WAN)
      if (audioFile && isWAN) {
        const uploadResult = await uploadApi.uploadTempFile(audioFile);
        if (uploadResult.success) {
          audioUrl = uploadResult.url;
        }
      }

      const settings: VideoSettings = {
        model,
        prompt,
        clip_type: clipType !== 'none' ? clipType : undefined,
        camera_control: cameraControl !== 'none' ? cameraControl : undefined,
      };

      if (model.startsWith('seedance')) {
        settings.aspect_ratio = aspectRatio;
        settings.resolution = resolution;
        settings.duration = duration;
        settings.camera_fixed = cameraFixed;
        settings.seed = seed;
        if (imageUrl) settings.image_url = imageUrl;
      } else if (isWAN) {
        settings.aspect_ratio = aspectRatio;
        settings.resolution = resolution;
        settings.duration = duration;
        settings.seed = seed;
        settings.enable_prompt_expansion = promptExpansion;
        if (negativePrompt) settings.negative_prompt = negativePrompt;
        if (imageUrl) settings.image_url = imageUrl;
        if (audioUrl) settings.audio_url = audioUrl;
      } else if (isHailuo) {
        settings.prompt_optimizer = promptOptimizer;
        if (imageUrl) settings.image_url = imageUrl;
      }

      const result = await scenesApi.generateVideo(projectId, sceneNumber, settings);

      if (result.success && result.task_id) {
        onTaskCreated(result.task_id);
        onClose();
      } else {
        alert('Failed to start video generation');
      }
    } catch (error: any) {
      console.error('Error generating video:', error);
      alert('Error: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={onClose}>
      <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Generate Video for Scene {sceneNumber}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {hasFirstFrame && (
          <div style={{ textAlign: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: '#28a745' }}>
              ✅ First Frame Generated
            </label>
            <img
              src={getFullUrlWithTimestamp(firstFrameUrl)}
              alt="First Frame"
              style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px', border: '2px solid #28a745' }}
            />
            <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
              This frame will be used as the starting image for Image-to-Video generation
            </p>
          </div>
        )}

        <div className="form-group">
          <label>Select Model:</label>
          <div className="model-selection" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '20px' }}>
            {['seedance-t2v', 'seedance-i2v', 'wan-t2v', 'wan-i2v', 'hailuo-t2v', 'hailuo-i2v'].map((m) => (
              <div
                key={m}
                className={`model-card ${model === m ? 'selected' : ''}`}
                onClick={() => setModel(m as VideoSettings['model'])}
                style={{
                  padding: '15px',
                  border: model === m ? '2px solid #007bff' : '2px solid #dee2e6',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  background: model === m ? '#e3f2fd' : 'white',
                }}
              >
                <h5>{m.toUpperCase().replace(/-/g, ' ')}</h5>
              </div>
            ))}
          </div>
        </div>

        {isI2V && !hasFirstFrame && (
          <div className="form-group">
            <label>Upload Image:</label>
            <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
          </div>
        )}

        {isWAN && (
          <div className="form-group">
            <label>Background Audio (optional):</label>
            <input type="file" accept="audio/*" onChange={(e) => setAudioFile(e.target.files?.[0] || null)} />
          </div>
        )}

        <div className="form-group">
          <label>Video Prompt:</label>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
          <div className="form-group">
            <label>Camera Control:</label>
            <select value={cameraControl} onChange={(e) => setCameraControl(e.target.value)}>
              <option value="none">None</option>
              <option value="tilt_pan_dolly">Tilt, Pan, Dolly Zoom</option>
              <option value="tracking_follow_orbit">Tracking, Follow, Orbit</option>
              <option value="push_pull_rapid_zoom">Push Pull Rapid Zoom</option>
              <option value="low_angle_arc_boom">Low Angle, Arc, Boom</option>
              <option value="into_blur">Into Blur</option>
              <option value="over_shoulder">Over The Shoulder</option>
              <option value="dutch_angle">Dutch Angle</option>
              <option value="overhead">Overhead</option>
              <option value="bird_eye">Bird's Eye</option>
              <option value="handheld">Handheld</option>
            </select>
          </div>

          <div className="form-group">
            <label>Clip Type:</label>
            <select value={clipType} onChange={(e) => setClipType(e.target.value)}>
              <option value="none">None</option>
              <option value="static_shot">Static Shot</option>
              <option value="story_loop">Story Loop</option>
              <option value="a_roll">A-Roll</option>
              <option value="b_roll">B-Roll</option>
              <option value="c_roll">C-Roll</option>
              <option value="ambient_scenes">Ambient Scenes</option>
              <option value="pov_hands">POV Hands</option>
              <option value="walk_and_talk">Walk and Talk</option>
              <option value="tabletop_shot">Tabletop Shot</option>
              <option value="vlogging_selfie">Vlogging Selfie</option>
              <option value="selfie_stick_shot">Selfie Stick Shot</option>
              <option value="interview_style">Interview Style</option>
              <option value="action_shot">Action Shot</option>
              <option value="flat_lay">Flat Lay</option>
              <option value="pop_hands">Pop Hands</option>
              <option value="talking_head">Talking Head</option>
            </select>
          </div>
        </div>

        {model.startsWith('seedance') && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            <div className="form-group">
              <label>Aspect Ratio:</label>
              <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)}>
                <option value="16:9">16:9</option>
                <option value="9:16">9:16</option>
                <option value="1:1">1:1</option>
              </select>
            </div>
            <div className="form-group">
              <label>Resolution:</label>
              <select value={resolution} onChange={(e) => setResolution(e.target.value)}>
                <option value="1080p">1080p</option>
                <option value="720p">720p</option>
              </select>
            </div>
            <div className="form-group">
              <label>Duration:</label>
              <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
                {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((d) => (
                  <option key={d} value={d}>{d}s</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                <input type="checkbox" checked={cameraFixed} onChange={(e) => setCameraFixed(e.target.checked)} />
                {' '}Fixed Camera
              </label>
            </div>
          </div>
        )}

        {isWAN && (
          <div>
            <div className="form-group">
              <label>Negative Prompt:</label>
              <textarea value={negativePrompt} onChange={(e) => setNegativePrompt(e.target.value)} rows={2} />
            </div>
            <div className="form-group">
              <label>
                <input type="checkbox" checked={promptExpansion} onChange={(e) => setPromptExpansion(e.target.checked)} />
                {' '}Enable Prompt Expansion
              </label>
            </div>
          </div>
        )}

        {isHailuo && (
          <div className="form-group">
            <label>
              <input type="checkbox" checked={promptOptimizer} onChange={(e) => setPromptOptimizer(e.target.checked)} />
              {' '}Prompt Optimizer (recommended)
            </label>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
          <button onClick={onClose} className="btn btn-secondary" disabled={generating}>
            Cancel
          </button>
          <button onClick={handleGenerate} className="btn btn-primary" disabled={generating}>
            {generating ? 'Generating...' : 'Generate Video'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
